import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { BRAND_CONFIGURATION_PATH } from "../../../routes/paths";
import { MEETING_APPS } from "../meetingApps";
import MeetingAppMark from "./MeetingAppMark";

/**
 * The strip an online appointment gets while no meeting app is connected:
 * where the link would have been made, and the way to make it possible.
 *
 * It stands in for the link itself. A connected app writes the meeting link
 * when the appointment is booked, so there is nothing to ask for here — what
 * is missing is not a URL, it is the connection, and a box asking for one
 * would be asking somebody to do by hand the thing an integration does for
 * them. So the space carries the way to set one up instead.
 *
 * A link and not a button, because it goes somewhere — and a router `Link`
 * rather than a plain anchor, because where it goes is a tab of this app.
 * The new tabs elsewhere in the panel are all links out of it — a meeting
 * URL, a terms page — and this is not one: connecting an app is part of the
 * same session as booking the appointment, so it is followed in place like
 * any other move between screens.
 *
 * It costs the half-filled dialog, which goes with the screen under it. That
 * is the trade the design asks for, and it is the ordinary one: nothing here
 * has been saved yet, and a booking is a minute's work to start again once
 * the app is connected.
 */

export interface ConnectAppPromptProps {
  className?: string;
}

const ConnectAppPrompt: React.FC<ConnectAppPromptProps> = ({ className = "" }) => (
  <Link
    to={BRAND_CONFIGURATION_PATH}
    className={`flex items-center justify-between gap-3 rounded-[6px] border border-line bg-[#FAFBFC] px-4 py-3 transition-colors hover:border-[#5BD3E1] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 ${className}`}
  >
    <span className="min-w-0">
      <span className="block text-[14px] text-ink">Connect your app</span>
      <span className="mt-0.5 block text-[12px] 3xl:text-[13px] text-ink-muted">
        Create meeting easily with your app
      </span>
    </span>

    {/* The marks say which apps without spending a line of the dialog naming
        them: a logo is read faster than "Google meet", and the dialog they
        sit in is a booking, not a settings screen.

        Only the ones that can actually be connected. An app still waiting on
        its review has a row on the Configuration tab saying so, which is a
        statement of what is coming; a logo here would instead be a promise
        that pressing through connects it, and the tab would then have to
        take it back. */}
    <span className="flex flex-shrink-0 items-center gap-2">
      {MEETING_APPS.filter((app) => app.available).map((app) => (
        <MeetingAppMark key={app.id} app={app.id} size={20} />
      ))}
      <ArrowRight aria-hidden="true" className="ml-1 h-4 w-4 text-ink-muted" />
    </span>
  </Link>
);

export default ConnectAppPrompt;
